import { Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo, useState } from 'react';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { createInpersonInterview } from 'src/services/candidate.service';
import Image from 'src/components/image';
import Iconify from 'src/components/iconify/iconify';

const useStyles = makeStyles(theme => ({
  boxDiv: {
    padding: 20,
    gap: 16,
    borderRadius: 6,
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
  },
  btn: {
    width: '100%',
  },
  addGuest: {
    background: theme.palette.background.primaryGrey1,
    width: '8rem',
    borderRadius: 21,
    color: theme.palette.background.primaryBlack1,
    '&:hover': {
      background: theme.palette.background.primaryGrey1,
    },
  },
}));

const CreateInpersonInterviewForm = ({ jobId, userId, setFormOpen }) => {
  const classes = useStyles();
  const theme = useTheme();

  const defaultValues = useMemo(
    () => ({
      interviewName: '',
      datetime: null,
      interviewerName: '',
      interviewerEmail: '',
      guestArray: [],
    }),
    [],
  );

  const InpersonSchema = Yup.object().shape({
    interviewName: Yup.string().required('Interview Name is required'),
    datetime: Yup.date().required('Date and time are required'),
    interviewerName: Yup.string().required('Interviewer name is required'),
    interviewerEmail: Yup.string()
      .email('Please enter a valid email')
      .required('Email is required'),
    guestArray: Yup.array().of(
      Yup.string()
        .email('Please enter a valid email')
        .required('Guest email cannot be empty'),
    ),
  });

  const ResumeMethods = useForm({
    resolver: yupResolver(InpersonSchema),
    defaultValues,
  });

  const [methods, setMethods] = useState(ResumeMethods);

  const { handleSubmit, control } = methods;

  const onSubmit = handleSubmit(async (data, event) => {
    console.log(data);
    const payload = {
      interview_name: data?.interviewName,
      interviewer_name: data?.interviewerName,
      interviewer_email: data?.interviewerEmail,
      scheduled_at: new Date(data?.datetime)?.getTime(),
      guest_email_list: data?.guestArray,
    };
    createInpersonInterview(jobId, userId, payload)
      .then(res => {
        console.log(res);
        setFormOpen(false);
      })
      .catch(err => {
        console.log(err);
      });
  });

  const deleteGuestHandler = index => {
    const dum = [...methods.getValues('guestArray')];
    dum.splice(index,1);
    methods.setValue('guestArray',dum);
  };

  return (
    <Stack spacing="24px">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack className={classes.boxDiv}>
          <Stack spacing="6px">
            <Typography variant="subtitle1">Interview title</Typography>
            <RHFTextField name="interviewName" />
          </Stack>
          <Stack spacing="6px">
            <Typography variant="subtitle1">Date and time</Typography>
            {/* <DateTimePicker
              disablePast
              views={['year', 'month', 'day', 'hours', 'minutes']}
            /> */}
            
            <Controller
              name="datetime"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <DateTimePicker
                    {...field}
                    disablePast
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    onChange={e => {
                      methods.setValue('datetime', e);
                    }}
                    closeOnSelect={false}
                  />
                  {error && (
                    <Typography
                      sx={{
                        marginLeft: '14px',
                        margintop: '8px',
                        color: theme.palette.content.primaryRed1,
                      }}
                      variant="caption"
                    >
                      {error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
          <Stack spacing="6px">
            <Typography variant="subtitle1">Interviewer name</Typography>
            <RHFTextField name="interviewerName" />
          </Stack>
          <Stack spacing="6px">
            <Typography variant="subtitle1">Interviewer email</Typography>
            <RHFTextField name="interviewerEmail" />
          </Stack>

          <Button
            size="small"
            variant="contained"
            startIcon={<Image src="/assets/users.svg" />}
            className={classes.addGuest}
            onClick={() => {
              methods.setValue('guestArray', [
                ...methods.getValues('guestArray'),
                '',
              ]);
            }}
          >
            Add guests
          </Button>

          <Controller
            name="guestArray"
            control={control}
            defaultValue={methods.getValues('guestArray')}
            render={({ field, fieldState: { error } }) => (
              <Stack spacing="6px">
                {field?.value?.map((fieldValue, index) => (
                  <Stack key={index} spacing="8px" direction="row" justifyContent="space-between" alignItems="center">
                    <TextField
                      name={`guestArray[${index}]`}
                      value={fieldValue}
                      onChange={e =>
                        field.onChange([
                          ...field.value.slice(0, index),
                          e.target.value,
                          ...field.value.slice(index + 1),
                        ])
                      }
                      variant="outlined"
                      placeholder="Enter email id"
                      fullWidth
                      error={!!error?.[index]}
                      helperText={error?.[index]?.message}
                    />
                    <Iconify
                      icon="tabler:x"
                      onClick={() => deleteGuestHandler(index)}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          />

          <Stack direction="row" spacing="12px">
            <Button
              variant="soft"
              size="large"
              width="100%"
              className={classes.btn}
              onClick={() => {
                setFormOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              type="submit"
              className={classes.btn}
            >
              Schedule
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default CreateInpersonInterviewForm;
